import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import apiDefinitions from '../../../../../core/api/apiDefinitions';

interface DepartmentAdminSpecificData {
  departmentNames?: string[];
}
interface UserSpecificDepartmentData { [key: string]: any; }
interface DriverSpecificData {
  licenseNumber?: string;
  licenseType?: string;
  expireDate?: string; // ISO
}
interface DetailedUser {
  empNumber: string;
  name: string;
  email?: string | null;
  status: string;
  phone?: string | null;
  createdBy?: string | null;
  createdAt?: string | null; // ISO
  group:
    | 'DEPARTMENT_HEAD'
    | 'USER'
    | 'DRIVER'
    | 'HR_MANAGER'
    | 'SERVICE_MANAGER'
    | 'MINOR_STAFF'
    | 'SECURITY'
    | string;
  departmentAdminSpecificData?: DepartmentAdminSpecificData | null;
  userSpecificDepartmentData?: UserSpecificDepartmentData | null;
  driverSpecificData?: DriverSpecificData | null;
}
interface LightweightUser {
  empNumber: string;
  name: string;
  createdAt: string;
  status: string;
  email?: string;
  phone?: string;
  userType?: string;
  departmentNames?: string[];
  licenseNumber?: string;
  licenseType?: string;
  expireDate?: string;
}
interface ViewUserModalProps {
  show: boolean;
  onHide: () => void;
  viewUser: LightweightUser | null; // we’ll use empNumber from this to fetch full details
}

const ViewUserModal: React.FC<ViewUserModalProps> = ({ show, onHide, viewUser }) => {
  const [details, setDetails] = useState<DetailedUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!show || !viewUser?.empNumber) return;
      setLoading(true);
      setErr(null);
      try {
        const res = await apiDefinitions.getUserByEmpNumber(viewUser.empNumber);
        if (res.data?.status === 'S1000' && res.data?.data) {
          setDetails(res.data.data as DetailedUser);
        } else {
          setErr(res.data?.statusDescription || 'Failed to load user details');
          setDetails(null);
        }
      } catch (e: any) {
        setErr(e?.response?.data?.statusDescription || e?.message || 'Network error');
        setDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [show, viewUser?.empNumber]);

  const showDeptBlocks =
    details?.group === 'DEPARTMENT_HEAD' || details?.group === 'USER';
  const showDriverBlock = details?.group === 'DRIVER';

  const formatDate = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleString();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>User Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>Loading…</span>
          </div>
        )}

        {!loading && err && <Alert variant="danger">{err}</Alert>}

        {!loading && !err && details && (
          <Row className="g-4">
            {/* LEFT: Core details */}
            <Col md={6}>
              {details.empNumber && (
                <Form.Group className="mb-3">
                  <Form.Label>Employee Number</Form.Label>
                  <Form.Control type="text" value={details.empNumber} readOnly />
                </Form.Group>
              )}
              {details.name && (
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control type="text" value={details.name} readOnly />
                </Form.Group>
              )}
              {details.email && (
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" value={details.email} readOnly />
                </Form.Group>
              )}
              {details.phone && (
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control type="text" value={details.phone} readOnly />
                </Form.Group>
              )}
              {details.createdAt && (
                <Form.Group className="mb-3">
                  <Form.Label>Created At</Form.Label>
                  <Form.Control type="text" value={formatDate(details.createdAt)} readOnly />
                </Form.Group>
              )}
             
            </Col>

            {/* RIGHT: Role-specific details */}
            <Col md={6}>
             {details.status && (
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Control type="text" value={details.status} readOnly />
                </Form.Group>
              )}
              {details.group && (
                <Form.Group className="mb-0">
                  <Form.Label>User Type</Form.Label>
                  <Form.Control type="text" value={details.group} readOnly />
                </Form.Group>
              )}
              {/* Department Admin + User-specific blocks */}
              {showDeptBlocks &&
              details.departmentAdminSpecificData?.departmentNames?.length ? (
                <Form.Group className="mb-3">
                  <Form.Label>Department(s)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={details.departmentAdminSpecificData.departmentNames.join(', ')}
                    readOnly
                  />
                </Form.Group>
              ) : null}

              {showDeptBlocks &&
              details.userSpecificDepartmentData &&
              Object.keys(details.userSpecificDepartmentData).length > 0 ? (
                <Form.Group className="mb-3">
                  <Form.Label>User-Specific Department Data</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={JSON.stringify(details.userSpecificDepartmentData, null, 2)}
                    readOnly
                  />
                </Form.Group>
              ) : null}

              {/* Driver-specific block */}
              {showDriverBlock && details.driverSpecificData ? (
                <>
                  {details.driverSpecificData.licenseNumber && (
                    <Form.Group className="mb-3">
                      <Form.Label>License Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={details.driverSpecificData.licenseNumber}
                        readOnly
                      />
                    </Form.Group>
                  )}
                  {details.driverSpecificData.licenseType && (
                    <Form.Group className="mb-3">
                      <Form.Label>License Type</Form.Label>
                      <Form.Control
                        type="text"
                        value={details.driverSpecificData.licenseType}
                        readOnly
                      />
                    </Form.Group>
                  )}
                  {details.driverSpecificData.expireDate && (
                    <Form.Group className="mb-0">
                      <Form.Label>License Expiration Date</Form.Label>
                      <Form.Control
                        type="text"
                        value={formatDate(details.driverSpecificData.expireDate)}
                        readOnly
                      />
                    </Form.Group>
                  )}
                </>
              ) : null}
            </Col>
          </Row>
        )}

        <div className="d-flex justify-content-end mt-3">
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ViewUserModal;
